import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { HeaderA } from "components/headers";
import { ModuleWrapper } from "components/fragments";
import { useApi, useFilter, useMount } from "hooks";
import SalesReportFilter from "./sales-report-filter";
import lang from "translations";
import { VenueContext } from "contexts";
import {
  salesReportDetailsRequest,
  salesReportDetailsResponse,
  salesReportOverviewResponse,
  salesReportRequest,
} from "mappers";
import { toApiDateTimeFormat } from "services/date.service";
import SalesCharts, {
  getAvailableTimeGrains,
  PEAK_ALL_FILTER,
  PEAK_FILTER_BY_VALUES,
} from "./sales-charts";
import { salesReportFilterState } from "./filters";
import {
  getSalesReportDetails,
  getSalesReportOverview,
  searchSalesReportExport,
} from "apis";
import { mapObject } from "services";
import { mixpanel, TrackEvent } from "mixpanel";
import { formatNumberToMoneyWithCurrencySymbol } from "services/money.service";
import styles from "./sales-report.module.scss";

const getFilterBy = ({ activeMetric, activeView, peakDay, timeGrain }) => {
  if (activeMetric === "creditFlow" || activeMetric === "paymentType") return null;
  if (activeView === "OVER_TIME") return timeGrain === "WEEK" ? "DAY" : timeGrain;
  if (activeView === "PEAK_HOUR") return peakDay;
  return activeView;
};

const getValidTimeGrain = (dateRange, currentTimeGrain) => {
  const availableTimeGrains = getAvailableTimeGrains(dateRange);
  if (availableTimeGrains.some((grain) => grain.id === currentTimeGrain)) return currentTimeGrain;
  return availableTimeGrains[0]?.id || "MONTH";
};

const buildReportParams = (filterState) => {
  const { dateRange } = filterState;
  return {
    ...filterState,
    startDateTime: toApiDateTimeFormat(dateRange[0]),
    endDateTime: toApiDateTimeFormat(dateRange[1], true),
  };
};

const numberText = (value) => new Intl.NumberFormat().format(Number(value) || 0);

const SalesReport = () => {
  const { venue, refreshDownloaderReports } = useContext(VenueContext);
  const initialFilterState = useMemo(() => salesReportFilterState(venue), [venue]);
  const { modifyFilter, modifyFilters, filterState, requestState } = useFilter(initialFilterState);
  const [submittedRequestState, setSubmittedRequestState] = useState(requestState);
  const [activeMetric, setActiveMetric] = useState("revenue");
  const [activeView, setActiveView] = useState("OVER_TIME");
  const [timeGrain, setTimeGrain] = useState("WEEK");
  const [peakDay, setPeakDay] = useState(PEAK_ALL_FILTER);
  const [peakAllDetails, setPeakAllDetails] = useState({
    data: null,
    loading: false,
    error: false,
  });
  const availableTimeGrains = useMemo(
    () => getAvailableTimeGrains(submittedRequestState.dateRange),
    [submittedRequestState.dateRange],
  );

  const filterBy = useMemo(
    () => getFilterBy({ activeMetric, activeView, peakDay, timeGrain }),
    [activeMetric, activeView, peakDay, timeGrain],
  );

  const { request: exportRequest, loading: exportLoading } = useApi({
    api: searchSalesReportExport,
    handleOwnError: true,
  });

  const overviewApi = useApi({
    api: getSalesReportOverview,
    mapper: salesReportOverviewResponse,
    handleOwnError: true,
  });

  const detailsApi = useApi({
    api: getSalesReportDetails,
    mapper: salesReportDetailsResponse,
    handleOwnError: true,
  });

  const buildOverviewBody = useCallback((state) => {
    return mapObject(buildReportParams(state), salesReportRequest);
  }, []);

  const buildDetailsBody = useCallback(
    (state, nextFilterBy = filterBy) => {
      if (!nextFilterBy) return null;
      return mapObject({ ...buildReportParams(state), filterBy: nextFilterBy }, salesReportDetailsRequest);
    },
    [filterBy],
  );

  const fetchOverview = useCallback(
    async (state) => {
      await overviewApi.request(buildOverviewBody(state));
    },
    [buildOverviewBody, overviewApi],
  );

  const fetchPeakAllDetails = useCallback(
    async (state) => {
      setPeakAllDetails({ data: null, loading: true, error: false });
      try {
        const responses = await Promise.all(
          PEAK_FILTER_BY_VALUES.map(async (peakFilterBy) => {
            const response = await getSalesReportDetails(buildDetailsBody(state, peakFilterBy));
            const mapped = mapObject({ ...(response?.data || {}) }, salesReportDetailsResponse);
            return mapped.peakHourDetailResponses || [];
          }),
        );

        setPeakAllDetails({
          data: { peakHourDetailResponses: responses.flat() },
          loading: false,
          error: false,
        });
      } catch {
        setPeakAllDetails({ data: null, loading: false, error: true });
      }
    },
    [buildDetailsBody],
  );

  const fetchDetails = useCallback(
    async (state, nextFilterBy = filterBy) => {
      if (nextFilterBy === PEAK_ALL_FILTER) {
        await fetchPeakAllDetails(state);
        return;
      }

      setPeakAllDetails((current) => ({ ...current, loading: false, error: false }));
      const body = buildDetailsBody(state, nextFilterBy);
      if (!body) return;
      await detailsApi.request(body);
    },
    [buildDetailsBody, detailsApi, fetchPeakAllDetails, filterBy],
  );

  const fetchReports = useCallback(
    async (state, nextFilterBy = filterBy) => {
      setSubmittedRequestState(state);
      await Promise.allSettled([fetchOverview(state), fetchDetails(state, nextFilterBy)]);
    },
    [fetchDetails, fetchOverview, filterBy],
  );

  useMount(() => {
    fetchReports(requestState, filterBy);

    mixpanel.track(TrackEvent.VisitedPage, {
      Page: lang.salesReport,
    });
  });

  useEffect(() => {
    if (!filterBy) return;
    fetchDetails(submittedRequestState, filterBy);
  }, [filterBy]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeView !== "OVER_TIME") return;
    if (availableTimeGrains.some((grain) => grain.id === timeGrain)) return;
    setTimeGrain(availableTimeGrains[0]?.id || "MONTH");
  }, [activeView, availableTimeGrains, timeGrain]);

  const applyFilter = useCallback(
    async (searchKey) => {
      const { requestState: nextRequestState } = modifyFilters({ page: 1, searchKey });
      const nextTimeGrain = getValidTimeGrain(nextRequestState.dateRange, timeGrain);
      const nextFilterBy = getFilterBy({
        activeMetric,
        activeView,
        peakDay,
        timeGrain: nextTimeGrain,
      });
      if (nextTimeGrain !== timeGrain) setTimeGrain(nextTimeGrain);
      await fetchReports(nextRequestState, nextFilterBy);
    },
    [activeMetric, activeView, fetchReports, modifyFilters, peakDay, timeGrain],
  );

  const clearFilter = useCallback(() => {
    const nextFilterState = salesReportFilterState(venue);
    const { requestState: nextRequestState } = modifyFilters(nextFilterState);
    const nextTimeGrain = getValidTimeGrain(nextRequestState.dateRange, timeGrain);
    const nextFilterBy = getFilterBy({
      activeMetric,
      activeView,
      peakDay,
      timeGrain: nextTimeGrain,
    });
    if (nextTimeGrain !== timeGrain) setTimeGrain(nextTimeGrain);
    fetchReports(nextRequestState, nextFilterBy);
    return { filterState: nextFilterState, requestState: nextRequestState };
  }, [activeMetric, activeView, fetchReports, modifyFilters, peakDay, timeGrain, venue]);

  const retry = useCallback(() => {
    fetchReports(submittedRequestState, filterBy);
  }, [fetchReports, filterBy, submittedRequestState]);

  const exportXlsx = useCallback(async () => {
    const body = {
      ...buildOverviewBody(submittedRequestState),
      requestedAt: new Date().toISOString(),
    };
    await exportRequest(body);
    // Refresh the download tray/badge right away instead of waiting for the
    // next websocket push, so the new export shows up immediately.
    refreshDownloaderReports?.();
  }, [buildOverviewBody, exportRequest, submittedRequestState, refreshDownloaderReports]);

  const overview = overviewApi.mappedData || {};
  const usingPeakAll = filterBy === PEAK_ALL_FILTER;
  const chartDetails = usingPeakAll ? peakAllDetails.data : detailsApi.mappedData;
  const chartDetailsLoading = usingPeakAll ? peakAllDetails.loading : detailsApi.loading;
  const chartDetailsError = usingPeakAll ? peakAllDetails.error : detailsApi.error;

  return (
    <ModuleWrapper>
      <HeaderA
        title={lang.salesReport}
        description={lang.viewTheSalesSummaryOfYourVenue}
        buttonsGroup={{
          label: lang.export,
          options: [lang.exportXlsx],
          loading: exportLoading,
          disabled: exportLoading,
          handleClick: exportXlsx,
        }}
        className="mb-md"
      />
      <SalesReportFilter
        filterState={filterState}
        requestState={requestState}
        modifyFilter={modifyFilter}
        modifyFilters={modifyFilters}
        onApply={applyFilter}
        onClear={clearFilter}
        loading={overviewApi.loading || detailsApi.loading || peakAllDetails.loading}
      />

      <div className={styles.kpis}>
        <div className={styles.kpiPrimary}>
          <span>{lang.totalSales}</span>
          <strong>{formatNumberToMoneyWithCurrencySymbol(overview.totalSales)}</strong>
        </div>
        <div className={styles.kpi}>
          <span>{lang.itemsSold}</span>
          <strong>{numberText(overview.itemsSold)}</strong>
        </div>
        <div className={styles.kpi}>
          <span>{lang.averageOrderValue}</span>
          <strong>{formatNumberToMoneyWithCurrencySymbol(overview.averageOrderValue)}</strong>
        </div>
        <div className={styles.kpiAccent}>
          <span>{lang.unspentCredit}</span>
          <strong>{formatNumberToMoneyWithCurrencySymbol(overview.unSpentCredit)}</strong>
        </div>
      </div>

      <SalesCharts
        activeMetric={activeMetric}
        activeView={activeView}
        dateRange={submittedRequestState.dateRange}
        details={chartDetails}
        detailsError={chartDetailsError}
        detailsLoading={chartDetailsLoading}
        filterBy={filterBy}
        onMetricChange={setActiveMetric}
        onPeakDayChange={setPeakDay}
        onRetry={retry}
        onTimeGrainChange={setTimeGrain}
        onViewChange={setActiveView}
        overview={overview}
        overviewError={overviewApi.error}
        overviewLoading={overviewApi.loading}
        peakDay={peakDay}
        timeGrain={timeGrain}
        timeGrains={availableTimeGrains}
      />
    </ModuleWrapper>
  );
};

export default SalesReport;
